import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  getAnnouncements as getAnnouncementsRequest,
  getAnnouncementById as getAnnouncementByIdRequest,
  deleteAnnouncement as deleteAnnouncementRequest,
} from 'src/services/client';

interface UseAnnouncementsAPIState {
  announcements: any[];
  isLoadingAnnouncements: boolean;
  announcementsError: string | null;
}

export interface FetchAnnouncementsResult {
  ok: boolean;
  announcements: any[];
}

export interface DeleteAnnouncementResult {
  ok: boolean;
}

export interface FetchAnnouncementByIdResult {
  ok: boolean;
  data: any;
}

export interface UseAnnouncementsAPIReturn {
  announcements: any[];
  isLoadingAnnouncements: boolean;
  announcementsError: string | null;
  fetchAnnouncements: (
    userId: string,
    role: string
  ) => Promise<FetchAnnouncementsResult>;
  fetchAnnouncementById: (id: string) => Promise<FetchAnnouncementByIdResult>;
  deleteAnnouncement: (id: string) => Promise<DeleteAnnouncementResult>;
}

const isOkResponse = (response: any): boolean =>
  response.ok || response.status === 200;

export const useAnnouncementsAPI = (): UseAnnouncementsAPIReturn => {
  const [state, setState] = useState<UseAnnouncementsAPIState>({
    announcements: [],
    isLoadingAnnouncements: false,
    announcementsError: null,
  });

  const fetchAnnouncements = async (
    userId: string,
    role: string
  ): Promise<FetchAnnouncementsResult> => {
    setState((prev: UseAnnouncementsAPIState) => ({
      ...prev,
      isLoadingAnnouncements: true,
      announcementsError: null,
    }));

    try {
      const response: any = await getAnnouncementsRequest(userId, role);

      if (!isOkResponse(response)) {
        throw new Error(`Failed to fetch announcements: ${response.status}`);
      }

      const payload: any = await response.json();

      if (payload.statusCode !== 200) {
        throw new Error(
          `Failed to fetch announcements: ${payload.statusCode}`
        );
      }

      const announcements: any[] = payload.data || [];

      setState((prev: UseAnnouncementsAPIState) => ({
        ...prev,
        announcements,
        isLoadingAnnouncements: false,
      }));

      return { ok: true, announcements };
    } catch (err: any) {
      console.error('useAnnouncementsAPI fetchAnnouncements failed:', err);

      const errorMessage: string =
        err?.message || 'Failed to fetch announcements';

      setState((prev: UseAnnouncementsAPIState) => ({
        ...prev,
        isLoadingAnnouncements: false,
        announcementsError: errorMessage,
      }));

      return { ok: false, announcements: [] };
    }
  };

  const fetchAnnouncementById = async (
    id: string
  ): Promise<FetchAnnouncementByIdResult> => {
    try {
      const response: any = await getAnnouncementByIdRequest(id);

      if (!isOkResponse(response)) {
        throw new Error(`Failed to fetch announcement: ${response.status}`);
      }

      const payload: any = await response.json();

      return { ok: true, data: payload.data };
    } catch (err: any) {
      console.error('useAnnouncementsAPI fetchAnnouncementById failed:', err);

      return { ok: false, data: null };
    }
  };

  const deleteAnnouncement = async (
    id: string
  ): Promise<DeleteAnnouncementResult> => {
    try {
      const response: any = await deleteAnnouncementRequest(id);

      if (!isOkResponse(response)) {
        throw new Error(`Failed to delete announcement: ${response.status}`);
      }

      const payload: any = await response.json();

      if (payload.statusCode !== 200) {
        throw new Error(
          `Failed to delete announcement: ${payload.statusCode}`
        );
      }

      setState((prev: UseAnnouncementsAPIState) => ({
        ...prev,
        announcements: prev.announcements.filter((a: any) => a.id !== id),
      }));

      toast.success('ⓘ Announcement has been deleted .', {
        autoClose: 10000,
        position: 'top-center',
      });

      return { ok: true };
    } catch (err: any) {
      console.error('useAnnouncementsAPI deleteAnnouncement failed:', err);
      toast.error(
        ' 🚫 Something went wrong with deleting a announcement, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );

      return { ok: false };
    }
  };

  return {
    announcements: state.announcements,
    isLoadingAnnouncements: state.isLoadingAnnouncements,
    announcementsError: state.announcementsError,
    fetchAnnouncements,
    fetchAnnouncementById,
    deleteAnnouncement,
  };
};
